import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import { ScrollText, RefreshCw } from "lucide-react";

const OUTCOME_STYLES = {
  success: "bg-green-100 text-green-700",
  failure: "bg-orange-100 text-orange-700",
  denied: "bg-red-100 text-red-700",
  pending: "bg-gray-100 text-gray-600",
};

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ action: "", actor_email: "", outcome: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/admin/audit-log", { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "You don't have audit access" : "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="admin-audit-log-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-[#1C3A7A] flex items-center gap-2">
            <ScrollText className="w-6 h-6" /> Audit Log
            <span className="text-sm font-normal text-gray-500">({total} entries)</span>
          </h1>
          <button data-testid="audit-refresh-btn" onClick={load}
                  className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <input data-testid="audit-filter-action" placeholder="Filter by action (e.g. coins.adjust)"
                 className="border rounded-lg px-3 py-1.5 text-sm w-60"
                 value={filters.action}
                 onChange={(e) => setFilters({ ...filters, action: e.target.value })} />
          <input data-testid="audit-filter-actor" placeholder="Filter by actor email"
                 className="border rounded-lg px-3 py-1.5 text-sm w-60"
                 value={filters.actor_email}
                 onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} />
          <select data-testid="audit-filter-outcome" className="border rounded-lg px-2 py-1.5 text-sm"
                  value={filters.outcome}
                  onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}>
            <option value="">All outcomes</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="denied">Denied</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Time (UTC)</th>
                <th className="px-4 py-2">Actor</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Target</th>
                <th className="px-4 py-2">Outcome</th>
                <th className="px-4 py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No audit entries match</td></tr>
              ) : entries.map((e) => (
                <tr key={e.id} className="border-t" data-testid="audit-log-row">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-500">{(e.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="px-4 py-2">{e.actor_email}<div className="text-xs text-gray-400">{e.actor_role}</div></td>
                  <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{e.target_type ? `${e.target_type}: ${e.target_id || ""}` : "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${OUTCOME_STYLES[e.outcome] || "bg-gray-100"}`}>{e.outcome}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate">{JSON.stringify(e.metadata || {})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
